import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { OpenedBid, ProcurementAward, Requisition, Tender } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const TENDER_FILTERS = ['', 'DRAFT', 'PUBLISHED', 'BIDS_CLOSED', 'EVALUATED', 'STANDSTILL', 'CONTRACTED'] as const;

export function Procurement() {
  const { persona } = useAuth();
  const { fy, voteByCode } = useRefData();
  const vote = voteByCode(persona?.voteCode);

  const isOfficer = persona?.roles.includes('MDA_PROCUREMENT_OFFICER') ?? false;
  const isNtb = persona?.roles.includes('NTB_SECRETARIAT') ?? false;
  const isEvaluator = persona?.roles.includes('EVALUATION_MEMBER') ?? false;

  const [statusFilter, setStatusFilter] = useState<(typeof TENDER_FILTERS)[number]>('');
  const [showRequisition, setShowRequisition] = useState(false);
  const [publishTarget, setPublishTarget] = useState<Tender | null>(null);
  const [openBidsTarget, setOpenBidsTarget] = useState<Tender | null>(null);
  const [evaluateTarget, setEvaluateTarget] = useState<Tender | null>(null);
  const [awardTarget, setAwardTarget] = useState<Tender | null>(null);
  const [contractTarget, setContractTarget] = useState<Tender | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [requisitions, setRequisitions] = useState<Requisition[]>(() => {
    const raw = sessionStorage.getItem('govfin.requisitions');
    return raw ? (JSON.parse(raw) as Requisition[]) : [];
  });
  const [openedBids, setOpenedBids] = useState<Record<string, OpenedBid[]>>({});
  const [awards, setAwards] = useState<Record<string, ProcurementAward>>({});

  const persistRequisitions = (items: Requisition[]) => {
    setRequisitions(items);
    sessionStorage.setItem('govfin.requisitions', JSON.stringify(items));
  };

  const tendersQ = useApiQuery<{ items: Tender[] }>(`/api/v1/procurement/tenders${statusFilter ? `?status=${statusFilter}` : ''}`);
  const combosQ = useApiQuery<{ items: Combination[] }>(vote ? `/api/v1/coa/combinations?voteId=${vote.id}&active=true&limit=100` : null);

  const draftTender = async (r: Requisition) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/procurement/requisitions/${r.id}/tender`);
      tendersQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft tender');
    }
  };

  return (
    <div>
      <PageHeader
        title="Procurement"
        subtitle="Threshold-driven procurement — requisition, tender, dual-control bid opening, evaluation, NTB award, contract"
        actions={
          isOfficer && vote && fy ? (
            <button className="btn-primary" onClick={() => setShowRequisition(true)}>
              + New requisition
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {isOfficer ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Requisitions (this session)</h2>
          <p className="mb-2 text-xs text-slate-400">
            There is no requisitions-listing endpoint yet — this app remembers what you create in this browser tab.
          </p>
          <div className="card overflow-x-auto">
            {requisitions.length === 0 ? (
              <EmptyState title="No requisitions yet" hint="Create one to run a funds check and get the procurement method." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Description</th>
                    <th className="table-head text-right">Estimate</th>
                    <th className="table-head">Method</th>
                    <th className="table-head">Approver</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {requisitions.map((r) => (
                    <tr key={r.id}>
                      <td className="table-cell max-w-sm truncate text-xs" title={r.description}>
                        {r.description}
                      </td>
                      <td className="table-cell text-right tabular-nums">{scr(r.estimate)}</td>
                      <td className="table-cell text-xs">{r.methodSuggested.replaceAll('_', ' ')}</td>
                      <td className="table-cell text-xs">{r.approver}</td>
                      <td className="table-cell text-right">
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => draftTender(r)}>
                          Draft tender
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        {TENDER_FILTERS.map((s) => (
          <button
            key={s || 'ALL'}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
              statusFilter === s ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
            }`}
            onClick={() => setStatusFilter(s)}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        {tendersQ.loading ? (
          <Spinner />
        ) : tendersQ.error ? (
          <ErrorNote message={tendersQ.error} />
        ) : (tendersQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No tenders" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Tender</th>
                <th className="table-head">Description</th>
                <th className="table-head">Method / Approver</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {tendersQ.data!.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">
                    {t.number}
                    {t.closeDate ? <div className="text-[11px] font-normal text-slate-400">closes {formatDate(t.closeDate)}</div> : null}
                  </td>
                  <td className="table-cell max-w-xs truncate text-xs" title={t.requisition?.description}>
                    {t.requisition?.description ?? '—'}
                  </td>
                  <td className="table-cell text-xs">
                    {t.method.replaceAll('_', ' ')} · {t.approver}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1.5">
                      {isOfficer && t.status === 'DRAFT' ? (
                        <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setPublishTarget(t)}>
                          Publish
                        </button>
                      ) : null}
                      {(isOfficer || isNtb) && t.status === 'PUBLISHED' ? (
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenBidsTarget(t)}>
                          Open bids
                        </button>
                      ) : null}
                      {isEvaluator && t.status === 'BIDS_CLOSED' ? (
                        openedBids[t.id] ? (
                          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setEvaluateTarget(t)}>
                            Evaluate
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-400">bids opened elsewhere</span>
                        )
                      ) : null}
                      {isNtb && t.status === 'EVALUATED' ? (
                        <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setAwardTarget(t)}>
                          Award
                        </button>
                      ) : null}
                      {isOfficer && t.status === 'STANDSTILL' ? (
                        awards[t.id] ? (
                          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setContractTarget(t)}>
                            Sign contract
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-400">award recorded elsewhere</span>
                        )
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRequisition && vote && fy ? (
        <RequisitionModal
          voteId={vote.id}
          fyId={fy.id}
          combos={combosQ.data?.items ?? []}
          onClose={() => setShowRequisition(false)}
          onDone={(r) => {
            setShowRequisition(false);
            persistRequisitions([r, ...requisitions]);
          }}
        />
      ) : null}
      {publishTarget ? (
        <PublishModal
          tender={publishTarget}
          onClose={() => setPublishTarget(null)}
          onDone={() => {
            setPublishTarget(null);
            tendersQ.reload();
          }}
        />
      ) : null}
      {openBidsTarget ? (
        <OpenBidsModal
          tender={openBidsTarget}
          onClose={() => setOpenBidsTarget(null)}
          onDone={(bids) => {
            setOpenedBids((prev) => ({ ...prev, [openBidsTarget.id]: bids }));
            setOpenBidsTarget(null);
            tendersQ.reload();
          }}
        />
      ) : null}
      {evaluateTarget ? (
        <EvaluateModal
          tender={evaluateTarget}
          bids={openedBids[evaluateTarget.id] ?? []}
          onClose={() => setEvaluateTarget(null)}
          onDone={() => {
            setEvaluateTarget(null);
            tendersQ.reload();
          }}
        />
      ) : null}
      {awardTarget ? (
        <AwardModal
          tender={awardTarget}
          onClose={() => setAwardTarget(null)}
          onDone={(award) => {
            setAwards((prev) => ({ ...prev, [awardTarget.id]: award }));
            setAwardTarget(null);
            tendersQ.reload();
          }}
        />
      ) : null}
      {contractTarget ? (
        <ContractModal
          award={awards[contractTarget.id]!}
          onClose={() => setContractTarget(null)}
          onDone={() => {
            setContractTarget(null);
            tendersQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function RequisitionModal({
  voteId,
  fyId,
  combos,
  onClose,
  onDone,
}: {
  voteId: string;
  fyId: string;
  combos: Combination[];
  onClose: () => void;
  onDone: (r: Requisition) => void;
}) {
  const [coaCombinationId, setCoaCombinationId] = useState('');
  const [description, setDescription] = useState('');
  const [estimate, setEstimate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const requisition = await api.post<Requisition>('/api/v1/procurement/requisitions', {
        voteId,
        fiscalYearId: fyId,
        coaCombinationId,
        description,
        estimate: Number.parseFloat(estimate),
      });
      onDone(requisition);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create requisition');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New procurement requisition" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Budget line">
          <select className="field-input" value={coaCombinationId} onChange={(e) => setCoaCombinationId(e.target.value)}>
            <option value="">Select budget line…</option>
            {combos.map((c) => (
              <option key={c.id} value={c.id}>
                {comboLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Estimate (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={estimate} onChange={(e) => setEstimate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !coaCombinationId || description.length < 10 || !estimate}>
            {busy ? 'Checking funds…' : 'Create requisition'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PublishModal({ tender, onClose, onDone }: { tender: Tender; onClose: () => void; onDone: () => void }) {
  const [publishDate, setPublishDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [closeDate, setCloseDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/procurement/tenders/${tender.id}/publish`, { publishDate, closeDate });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Publish ${tender.number}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Publish date">
            <input className="field-input" type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} />
          </Field>
          <Field label="Close date">
            <input className="field-input" type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !closeDate}>
            {busy ? 'Publishing…' : 'Publish to Gazette & public portal'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function OpenBidsModal({ tender, onClose, onDone }: { tender: Tender; onClose: () => void; onDone: (bids: OpenedBid[]) => void }) {
  const [witnessSubject, setWitnessSubject] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ tenderId: string; bids: OpenedBid[] }>(`/api/v1/procurement/tenders/${tender.id}/open-bids`, { witnessSubject });
      onDone(result.bids);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open bids');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Open bids — ${tender.number}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Dual control: a second officer must witness the opening, sealed until now.</p>
        <Field label="Witnessing officer (SeyID subject, must differ from you)">
          <input className="field-input" value={witnessSubject} onChange={(e) => setWitnessSubject(e.target.value)} placeholder="e.g. seyid:ntb-secretariat" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || witnessSubject.length < 3}>
            {busy ? 'Opening…' : 'Open bids'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EvaluateModal({
  tender,
  bids,
  onClose,
  onDone,
}: {
  tender: Tender;
  bids: OpenedBid[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [scores, setScores] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/procurement/tenders/${tender.id}/evaluate`, {
        scores: bids.filter((b) => scores[b.id]).map((b) => ({ bidId: b.id, score: Number.parseFloat(scores[b.id]!) })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Evaluation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Evaluate bids — ${tender.number}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {bids.map((b) => (
          <div key={b.id} className="flex items-center gap-3">
            <span className="flex-1 text-sm">
              Vendor {b.vendorId} — {scr(b.amount)}
            </span>
            <input
              className="field-input w-24"
              type="number"
              min="0"
              max="100"
              placeholder="Score"
              value={scores[b.id] ?? ''}
              onChange={(e) => setScores({ ...scores, [b.id]: e.target.value })}
            />
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || Object.keys(scores).length === 0}>
            {busy ? 'Saving…' : 'Save scores'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AwardModal({ tender, onClose, onDone }: { tender: Tender; onClose: () => void; onDone: (award: ProcurementAward) => void }) {
  const [ntbMinutesRef, setNtbMinutesRef] = useState('');
  const [standstillDays, setStandstillDays] = useState('10');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const award = await api.post<ProcurementAward>(`/api/v1/procurement/tenders/${tender.id}/award`, {
        ntbMinutesRef,
        standstillDays: Number.parseInt(standstillDays, 10),
      });
      onDone(award);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Award failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`NTB award — ${tender.number}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Awards the top-scored bid and opens the standstill window.</p>
        <Field label="NTB minutes reference">
          <input className="field-input" value={ntbMinutesRef} onChange={(e) => setNtbMinutesRef(e.target.value)} placeholder="e.g. NTB/MIN/2026/047" />
        </Field>
        <Field label="Standstill days (7–30)">
          <input className="field-input" type="number" min="7" max="30" value={standstillDays} onChange={(e) => setStandstillDays(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || ntbMinutesRef.length < 3}>
            {busy ? 'Awarding…' : 'Award tender'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ContractModal({ award, onClose, onDone }: { award: ProcurementAward; onClose: () => void; onDone: () => void }) {
  const [milestones, setMilestones] = useState([{ title: '', due: '' }]);
  const [retentionPercent, setRetentionPercent] = useState('10');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/procurement/awards/${award.id}/contract`, {
        milestones: milestones.filter((m) => m.title && m.due),
        retentionPercent: Number.parseFloat(retentionPercent),
        asOf,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Contract signing failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Sign contract" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Signing creates the F04 commitment for {scr(award.amount)}, welded to budget cover.</p>
        <div>
          <div className="field-label">Milestones</div>
          {milestones.map((m, idx) => (
            <div key={idx} className="mb-2 flex gap-2">
              <input className="field-input flex-1" placeholder="Title" value={m.title} onChange={(e) => setMilestones(milestones.map((r, i) => (i === idx ? { ...r, title: e.target.value } : r)))} />
              <input className="field-input w-40" type="date" value={m.due} onChange={(e) => setMilestones(milestones.map((r, i) => (i === idx ? { ...r, due: e.target.value } : r)))} />
              <button className="btn-secondary !px-2.5" onClick={() => setMilestones(milestones.filter((_, i) => i !== idx))}>
                ✕
              </button>
            </div>
          ))}
          <button className="btn-secondary !py-1.5 text-xs" onClick={() => setMilestones([...milestones, { title: '', due: '' }])}>
            + Add milestone
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Retention %">
            <input className="field-input" type="number" min="0" max="20" value={retentionPercent} onChange={(e) => setRetentionPercent(e.target.value)} />
          </Field>
          <Field label="As of (must be after standstill ends)">
            <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || milestones.every((m) => !m.title)}>
            {busy ? 'Signing…' : 'Sign contract'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
